import React from 'react';
import { motion } from 'framer-motion';
import { Truck, FlaskConical, ScanLine } from 'lucide-react';
import { DeliveryTruckIllustration } from './DeliveryTruckIllustration';
import { SanitizationIllustration } from './SanitizationIllustration';
import { TrackingIllustration } from './TrackingIllustration';
interface Scene {
  image?: string;
  custom?: 'truck' | 'sanitization' | 'tracking';
  alt: string;
  icon: React.ReactNode;
  label: string;
  title: string;
  desc: string;
  tint: 'sky' | 'leaf' | 'accent';
}
const scenes: Scene[] = [{
  custom: 'truck',
  alt: 'Delivery truck carrying stacked crates of returnable packaging',
  icon: <Truck className="w-5 h-5" data-id="element-20" />,
  label: 'Distribution',
  title: 'Out for delivery, back for pickup',
  desc: 'Our trucks drop clean bottles, crates, and pallets at your dock and grab the empties on the way out. One trip, both directions.',
  tint: 'sky'
}, {
  custom: 'sanitization',
  alt: 'Returnable packaging moving through a bulk industrial cleaning line at 99% cleanliness',
  icon: <FlaskConical className="w-5 h-5" data-id="element-21" />,
  label: 'Sanitization',
  title: "Cleaned like it's brand new",
  desc: "Everything that comes back goes through a serious wash and a quality check. It doesn't head back out until it's spotless.",
  tint: 'leaf'
}, {
  custom: 'tracking',
  alt: 'Returnable asset being scanned and routed into a crate',
  icon: <ScanLine className="w-5 h-5" data-id="element-22" />,
  label: 'Tracking',
  title: 'We always know where it is',
  desc: 'Every asset gets logged as it moves through filling, shipping, return, and cleaning. Nothing disappears on our watch.',
  tint: 'accent'
}];
const tintMap = {
  sky: {
    badge: 'bg-sky-soft text-sky',
    border: 'border-sky/20'
  },
  leaf: {
    badge: 'bg-mint text-leaf',
    border: 'border-leaf/20'
  },
  accent: {
    badge: 'bg-accent/15 text-[#8a5d18]',
    border: 'border-accent/30'
  }
};
export const BehindTheScenes: React.FC = () => {
  return <section className="py-32 bg-surface" data-id="element-23">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-24">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-2xl mb-16" data-id="element-25">
          <h2 className="font-display text-4xl md:text-5xl text-primary mb-6" data-id="element-26">
            Real operations, real infrastructure.
          </h2>
          <p className="text-lg text-ink/70" data-id="element-27">
            Reuse only works if the logistics behind it do. So we built the
            trucks, the wash lines, and the tracking to keep it all running.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8" data-id="element-28">
          {scenes.map((scene, index) => {
          const tint = tintMap[scene.tint];
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.12
          }} className={`group rounded-2xl overflow-hidden bg-white border ${tint.border} shadow-glossy hover:shadow-glossy-lg transition-all duration-500 hover:-translate-y-1`} data-id="element-29">
                <div className="relative h-64 overflow-hidden" data-id="element-30">
                  {scene.custom === 'truck' ? <DeliveryTruckIllustration data-id="element-31" /> : scene.custom === 'sanitization' ? <SanitizationIllustration data-id="element-32" /> : scene.custom === 'tracking' ? <TrackingIllustration data-id="element-33" /> : <>
                      <img src={scene.image} alt={scene.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-34" />
                      <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent" data-id="element-35" />
                    </>}
                  <div className={`absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider ${tint.badge} backdrop-blur-md z-10`} data-id="element-36">
                    {scene.icon}
                    {scene.label}
                  </div>
                </div>
                <div className="p-6" data-id="element-37">
                  <h3 className="font-display text-xl text-primary mb-2" data-id="element-38">
                    {scene.title}
                  </h3>
                  <p className="text-sm text-ink/70 leading-relaxed" data-id="element-39">
                    {scene.desc}
                  </p>
                </div>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
};